/**
 * Cacles · Cajón del carrito
 * Cross-sell (carrusel + añadir), servicios de 2 € y correcciones automáticas.
 * Cada acción es 1 sola petición al Cart API, que ya devuelve el cajón pintado.
 */
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class CcCart extends HTMLElement {
  static observedAttributes = ['data-sig'];
  static justAdded = null;
  static lastFix = '';

  connectedCallback() {
    if (!this.ccBound) {
      this.ccBound = true;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      // Recalcula la altura de la lista al abrir el cajón, al cambiar el carrito o al redimensionar
      this.ccRO = new ResizeObserver(() => this.scheduleFit());
    }

    const dialog = this.closest('dialog');
    if (dialog && !dialog.ccBound) {
      dialog.ccBound = true;
      dialog.addEventListener('close', () => {
        CcCart.justAdded = null;
      });
      // Al abrir el cajón se vuelve a mirar la fecha de preventa (Timesact puede haberla pintado ya)
      new MutationObserver(() => {
        if (dialog.open) dialog.querySelector('cc-cart')?.preorders(true);
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }

    this.schedule();
  }

  attributeChangedCallback() {
    this.schedule();
  }

  schedule() {
    if (this.ccRaf) return;
    this.ccRaf = requestAnimationFrame(() => {
      this.ccRaf = 0;
      if (this.isConnected) this.sync();
    });
  }

  /* Tras cada repintado del carrito */
  sync() {
    // El cross-sell recién añadido se queda visible con «Añadido» mientras el cajón siga abierto
    const pid = CcCart.justAdded;
    if (pid) {
      const slide = this.querySelector(`[data-cc-slide][data-pid="${pid}"]`);
      if (slide) {
        slide.hidden = false;
        slide.querySelector('[data-cc-add]')?.classList.add('is-added');
      }
    }
    this.carousel(pid);
    this.preorders(true);
    this.fitItems();

    // Correcciones: quitar prioritario o envío por separado si ya no aplican, quitar servicios si no hay productos, cantidad 1
    const fix = this.dataset.fix || '';
    if (fix && fix !== CcCart.lastFix) {
      CcCart.lastFix = fix;
      const msg = this.dataset.fixMsg || '';
      let updates = null;
      try {
        updates = JSON.parse(fix);
      } catch (e) {
        /* JSON inválido: no hacemos nada */
      }
      if (updates) {
        this.send('update', { updates }).then((ok) => {
          // Se explica al cliente por qué ha cambiado el total
          if (ok && msg) document.querySelector('dialog cc-cart')?.toast(msg, true);
        });
      }
      return;
    }
    if (!fix) CcCart.lastFix = '';

    // Si ya no queda ningún accesorio del cross-sell, se quita su código de descuento
    if (this.dataset.xsCode && this.dataset.xsCount === '0' && CcCart.codeFlag()) {
      CcCart.codeFlag(false);
      this.send('update', { discount: this.codes(false).join(',') });
    }
  }

  /* ---------- Código de descuento del cross-sell ---------- */
  codes(withXs) {
    const code = (this.dataset.xsCode || '').trim();
    const list = (this.dataset.codes || '')
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c && c.toLowerCase() !== code.toLowerCase());
    if (withXs && code) list.push(code);
    return list;
  }

  static codeFlag(value) {
    try {
      if (value === undefined) return sessionStorage.getItem('cc-xs-code') === '1';
      if (value) sessionStorage.setItem('cc-xs-code', '1');
      else sessionStorage.removeItem('cc-xs-code');
    } catch (e) {
      return false;
    }
  }

  /* ---------- Preventa: fecha de Timesact en las líneas cuyo plan no la lleva ---------- */
  static MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
  static MONTH_INDEX = {
    ene: 0, enero: 0, feb: 1, febrero: 1, mar: 2, marzo: 2, abr: 3, abril: 3, may: 4, mayo: 4,
    jun: 5, junio: 5, jul: 6, julio: 6, ago: 7, agosto: 7, sep: 8, sept: 8, set: 8, septiembre: 8,
    setiembre: 8, oct: 9, octubre: 9, nov: 10, noviembre: 10, dic: 11, diciembre: 11,
  };

  // «Disponibilidad: envío 13 de octubre de 2026» → «Se envía el 13 oct» (también «13 oct» y «13/10/2026»)
  static preDate(text) {
    const re = /(\d{1,2})(?:\s+(?:de\s+)?([a-z]{3,10})\.?|\/(\d{1,2})\/\d{2,4})/g;
    const raw = (text || '').toLowerCase();
    const out = [];
    let m;
    while ((m = re.exec(raw)) && out.length < 2) {
      const month = m[2] ? CcCart.MONTH_INDEX[m[2]] : Number(m[3]) - 1;
      if (month == null || !(month >= 0 && month <= 11)) continue;
      const label = `${Number(m[1])} ${CcCart.MONTHS[month]}`;
      if (!out.includes(label)) out.push(label);
    }
    if (!out.length) return '';
    return out.length > 1 ? `Se envía entre el ${out[0]} y el ${out[1]}` : `Se envía el ${out[0]}`;
  }

  static preMap(map) {
    try {
      if (map === undefined) return JSON.parse(localStorage.getItem('cc-pre') || '{}') || {};
      localStorage.setItem('cc-pre', JSON.stringify(map));
    } catch (e) {
      return {};
    }
  }

  // Ficha de producto abierta: fecha que pinta Timesact + qué planes y tallas son de ese producto
  static liveDate() {
    // Puede haber varios elementos de Timesact (algunos vacíos): vale el primero que traiga fecha
    let text = '';
    for (const el of document.querySelectorAll('.timesact-preorder-description, [class*="timesact"][class*="description"]')) {
      if (el.closest('cc-cart')) continue;
      text = CcCart.preDate(el.textContent);
      if (text) break;
    }
    if (!text) return null;

    // Planes y tallas del selector de la ficha (no de productos relacionados ni miniaturas)
    const plans = new Set();
    const vids = new Set();
    document.querySelectorAll('.delivery-sizes [data-delivery-json]').forEach((json) => {
      try {
        const d = JSON.parse(json.textContent);
        Object.keys(d.plans || {}).forEach((k) => plans.add(k));
        (d.variants || []).forEach((v) => vids.add(String(v.id)));
      } catch (e) {
        /* JSON inválido: se ignora */
      }
    });
    // Sin ese selector: tallas de los formularios de compra de la página
    if (!vids.size) {
      document.querySelectorAll('form[action*="/cart/add"] [name="id"]').forEach((input) => {
        if (input.value) vids.add(String(input.value));
      });
    }
    return { text, plans, vids };
  }

  static onProductPage() {
    return Boolean(document.querySelector('.delivery-sizes [data-delivery-json], form[action*="/cart/add"] [name="id"]'));
  }

  preorders(fresh) {
    if (fresh) this.ccPreTries = 0;
    const lines = [...this.querySelectorAll('[data-cc-pre]')];
    if (!lines.length) return;

    const map = CcCart.preMap();
    const live = CcCart.liveDate();
    const now = Date.now();
    let save = false;

    // 1) Ficha abierta de ese producto: se guarda la fecha de Timesact (por plan, sirve para todas sus tallas)
    if (live) {
      lines.forEach((el) => {
        const plan = el.dataset.ccPre;
        const vid = el.dataset.ccPreVid;
        if (!(plan && live.plans.has(plan)) && !live.vids.has(vid)) return;
        const key = plan || `v${vid}`;
        if (!map[key] || map[key].t !== live.text) {
          map[key] = { t: live.text, at: now };
          save = true;
        }
      });
    }

    // 2) Se pinta la fecha guardada (válida 10 días); si no hay, se queda el texto de respaldo
    let missing = false;
    lines.forEach((el) => {
      const hit = map[el.dataset.ccPre] || map[`v${el.dataset.ccPreVid}`];
      if (hit && now - hit.at < 864e6) {
        if (el.textContent !== hit.t) el.textContent = hit.t;
      } else {
        missing = true;
      }
    });

    if (save) {
      const keep = Object.entries(map)
        .sort((a, b) => b[1].at - a[1].at)
        .slice(0, 40);
      CcCart.preMap(Object.fromEntries(keep));
    }

    // Timesact pinta su texto unos segundos después de cargar: reintentos solo en fichas de producto
    if (missing && (this.ccPreTries || 0) < 6 && CcCart.onProductPage()) {
      this.ccPreTries = (this.ccPreTries || 0) + 1;
      clearTimeout(this.ccPreTimer);
      this.ccPreTimer = setTimeout(() => {
        if (this.isConnected) this.preorders(false);
      }, 1000);
    }
  }

  /* ---------- Escritorio: 2 productos a la vista, desde el 3º se desliza ---------- */
  scheduleFit() {
    if (this.ccFit) return;
    this.ccFit = requestAnimationFrame(() => {
      this.ccFit = 0;
      if (this.isConnected) this.fitItems();
    });
  }

  fitItems() {
    const box = this.querySelector('.cc-items');
    if (!box) return;

    if (box !== this.ccBox) {
      if (this.ccBox) {
        this.ccRO.unobserve(this.ccBox);
        this.wheel(this.ccBox, false);
      }
      this.ccRO.observe(box);
      this.ccBox = box;
    }

    if (!box.getClientRects().length) return; // cajón cerrado

    const rows = [...box.querySelectorAll('tbody > .cart-items__table-row')].filter((r) => !r.hidden && r.offsetHeight > 0);
    const cap = window.matchMedia('(min-width: 750px)').matches && rows.length > 2;
    let more = box.nextElementSibling && box.nextElementSibling.matches('[data-cc-more]') ? box.nextElementSibling : null;

    if (!cap) {
      box.classList.remove('is-capped', 'is-end');
      box.style.removeProperty('--cc-items-max');
      this.wheel(box, false);
      if (more) more.hidden = true;
      return;
    }

    const max = Math.ceil(rows[1].getBoundingClientRect().bottom - box.getBoundingClientRect().top + box.scrollTop);
    if (box.style.getPropertyValue('--cc-items-max') !== `${max}px`) box.style.setProperty('--cc-items-max', `${max}px`);
    box.classList.add('is-capped');
    this.wheel(box, true);

    if (!more) {
      more = document.createElement('button');
      more.type = 'button';
      more.className = 'cc-more';
      more.setAttribute('data-cc-more', '');
      more.innerHTML =
        '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5"/></svg><span></span>';
      box.after(more);
    }
    const n = rows.length - 2;
    more.lastElementChild.textContent = `Desliza para ver ${n} producto${n > 1 ? 's' : ''} más`;

    this.edge(box);
  }

  edge(box) {
    const end = box.scrollTop + box.clientHeight >= box.scrollHeight - 2;
    box.classList.toggle('is-end', end);
    const more = box.nextElementSibling;
    if (more && more.matches('[data-cc-more]')) more.hidden = end || !box.classList.contains('is-capped');
  }

  /* Rueda / trackpad: solo se escucha mientras la lista está limitada (no penaliza el scroll normal) */
  wheel(box, on) {
    if (Boolean(box.ccWheel) === on) return;
    box.ccWheel = on;
    if (on) box.addEventListener('wheel', this.onWheel, { passive: false });
    else box.removeEventListener('wheel', this.onWheel);
  }

  /* Al llegar al final (o al principio) de la lista, el mismo gesto sigue moviendo el carrito */
  onWheel = (e) => {
    const box = e.currentTarget;
    const body = box.closest('.cc-body');
    if (!body || e.ctrlKey || !box.classList.contains('is-capped')) return;
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? box.clientHeight : 1);
    if (!dy) return;

    const room = dy > 0 ? box.scrollHeight - box.clientHeight - box.scrollTop : -box.scrollTop;
    if (dy > 0 ? room >= dy : room <= dy) return; // la lista absorbe todo el gesto: scroll nativo

    e.preventDefault();
    if (Math.abs(room) >= 1) box.scrollTop += room;
    body.scrollTop += dy - room;
  };

  /* ---------- Carrusel ---------- */
  carousel(pid) {
    const xs = this.querySelector('[data-cc-xs]');
    if (!xs) return;

    const track = xs.querySelector('[data-cc-track]');
    const slides = [...track.children].filter((s) => s.hasAttribute('data-cc-slide') && !s.hidden);

    xs.hidden = slides.length === 0;
    xs.classList.toggle('is-single', slides.length < 2);

    const dots = xs.querySelector('[data-cc-dots]');
    if (dots && dots.childElementCount !== slides.length) dots.innerHTML = '<i></i>'.repeat(slides.length);

    if (pid) {
      const slide = slides.find((s) => s.dataset.pid === String(pid));
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
    }

    this.paint(xs);
  }

  paint(xs) {
    if (!xs) return;
    const track = xs.querySelector('[data-cc-track]');
    const dots = xs.querySelector('[data-cc-dots]');
    const n = dots ? dots.childElementCount : 0;
    const i = Math.min(Math.max(n - 1, 0), Math.max(0, Math.round(track.scrollLeft / (track.clientWidth || 1))));

    if (dots) [...dots.children].forEach((d, k) => d.classList.toggle('is-on', k === i));

    const prev = xs.querySelector('[data-cc-prev]');
    const next = xs.querySelector('[data-cc-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= n - 1;
  }

  onScroll = (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.matches('.cc-items')) {
      this.edge(t);
      return;
    }
    if (!t.matches('[data-cc-track]')) return;
    if (this.ccScroll) return;
    this.ccScroll = requestAnimationFrame(() => {
      this.ccScroll = 0;
      this.paint(t.closest('[data-cc-xs]'));
    });
  };

  /* ---------- Eventos ---------- */
  onClick = (e) => {
    const b = e.target.closest('[data-cc-prev], [data-cc-next], [data-cc-add], [data-cc-more]');
    if (!b || !this.contains(b)) return;

    if (b.hasAttribute('data-cc-more')) {
      const box = this.querySelector('.cc-items');
      if (box) box.scrollBy({ top: box.clientHeight, behavior: 'smooth' });
      return;
    }

    if (b.hasAttribute('data-cc-add')) {
      this.addXs(b);
      return;
    }

    const track = b.closest('[data-cc-xs]').querySelector('[data-cc-track]');
    const dir = b.hasAttribute('data-cc-next') ? 1 : -1;
    track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
  };

  onChange = (e) => {
    const t = e.target;

    if (t.matches('[data-cc-variant]')) {
      e.stopPropagation();
      const o = t.selectedOptions && t.selectedOptions[0];
      const slide = t.closest('[data-cc-slide]');
      if (!o || !slide) return;
      const was = slide.querySelector('[data-cc-was]');
      const now = slide.querySelector('[data-cc-now]');
      if (was && o.dataset.was) was.textContent = o.dataset.was;
      if (now && o.dataset.now) now.textContent = o.dataset.now;

      // Plazo de esa talla: inmediato o fecha de la marca
      const slow = o.dataset.slow === '1';
      const ship = slide.querySelector('[data-cc-ship]');
      if (ship) ship.textContent = slow ? ship.dataset.slow : ship.dataset.fast;
      slide.classList.toggle('is-slow', slow);
      return;
    }

    if (t.matches('[data-cc-svc]')) {
      e.stopPropagation();
      this.toggleSvc(t);
    }
  };

  /* ---------- Acciones ---------- */
  async addXs(btn) {
    if (btn.disabled || btn.classList.contains('is-added')) return;

    const slide = btn.closest('[data-cc-slide]');
    const id = Number(slide?.querySelector('[data-cc-variant]')?.value);
    if (!id) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    const done = () => {
      CcCart.justAdded = slide.dataset.pid;
      btn.classList.add('is-added');
    };

    // Desde el cajón: se añade y se aplica el código del −10 % en la misma petición, sin tocar los códigos del cliente
    const ok = this.dataset.xsCode
      ? await this.send('update', { updates: { [id]: 1 }, discount: this.codes(true).join(',') }, () => {
          CcCart.codeFlag(true);
          done();
        })
      : await this.send('add', { items: [{ id, quantity: 1 }] }, done);

    btn.classList.remove('is-loading');
    if (!ok) btn.disabled = false;
  }

  async toggleSvc(input) {
    const on = input.checked;
    input.disabled = true;

    const ok = on
      ? await this.send('add', { items: [{ id: Number(input.dataset.variant), quantity: 1 }] })
      : await this.send('change', { id: input.dataset.key, quantity: 0 });

    if (!ok) input.checked = !on;
    input.disabled = false;
  }

  /* 1 petición: Cart API + HTML del cajón en la misma respuesta */
  async send(action, body, onOk) {
    const sid = this.closest('cart-items-component')?.dataset.sectionId || this.dataset.sectionId;
    body.sections = [sid];
    body.sections_url = window.location.pathname;

    this.classList.add('is-busy');

    try {
      const res = await fetch(`${ROOT}cart/${action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'No se ha podido actualizar el carrito');
      }

      if (onOk) onOk(data);

      const html = (data.sections && data.sections[sid]) || '';
      const count = html.match(/data-cc-count="(\d+)"/);
      const itemCount = typeof data.item_count === 'number' ? data.item_count : count ? Number(count[1]) : undefined;

      // Horizon repinta el cajón y la burbuja del icono con este evento
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'cacles-cart',
            data: { source: 'cacles-cart', itemCount, sections: data.sections },
          },
        })
      );

      CcCart.ensure(html);
      return true;
    } catch (err) {
      this.toast(err.message);
      return false;
    } finally {
      this.classList.remove('is-busy');
    }
  }

  /* Red de seguridad: si el tema no repinta, cambiamos el bloque nosotros */
  static ensure(html) {
    const want = (html.match(/data-sig="([^"]+)"/) || [])[1];
    if (!want) return;

    setTimeout(() => {
      const cur = document.querySelector('dialog cc-cart');
      if (!cur || cur.dataset.sig === want) return;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const next = tpl.content.querySelector('dialog cc-cart');
      if (next) cur.replaceWith(next);
    }, 800);
  }

  toast(msg, info) {
    const el = this.querySelector('[data-cc-msg]');
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle('is-info', Boolean(info));
    el.hidden = false;
    clearTimeout(this.ccToast);
    this.ccToast = setTimeout(() => {
      el.hidden = true;
    }, info ? 7000 : 4000);
  }
}

if (!customElements.get('cc-cart')) customElements.define('cc-cart', CcCart);